/// <amd-module name="Wit/SprintsHub/Common/Contracts/Interfaces" />
import { WorkItemStateCategory } from "Agile/Common/Redux/BacklogConfiguration/BacklogConfigurationContracts";
import { ITeam } from "Agile/Contracts/Common";
import { IObservableValue } from "VSS/Core/Observable";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { Activity, DateRange, FilterClause, ParentChildWIMap, TeamSettingsDataContractBase } from "Wit/Clients/Work/Contracts/Work";
import { IClientWorkItem } from "Wit/ClientWorkItemService/IClientWorkItem";
import { FieldType } from "Wit/Common/Generated/Constants";
import { WorkItemSignalREventType } from "Wit/SprintsHub/Common/Constants";

export interface ITaskboardWorkItem extends IClientWorkItem {
    filteredOut: IObservableValue<boolean>;
    isTitleEditing: IObservableValue<boolean>;
    grayedOut: IObservableValue<boolean>;
    queuedForRemoving: IObservableValue<boolean>;
    parentId: IObservableValue<number>;
    state: IObservableValue<string>;
}

export enum CardIdentityFieldType {
    Identity = 13
}

export type CardFieldType = FieldType | CardIdentityFieldType;

export interface ICapacity {
    userCapacities: IUserCapacity[];
    teamDaysOff: IDaysOff[];
    exceptionInfo?: ExceptionInfo;
}

export interface IUserCapacity extends TeamSettingsDataContractBase {
    /**
     * Identity ref of the associated team member
     */
    teamMemberIdentityRef: IdentityRef;

    /**
     * Deprecated - associated team member
     */
    teamMember: IUser;
    activities: Activity[];
    daysOff: IDaysOff[];
}

export interface IDaysOff extends DateRange {
    ratio: number;
}

export interface IUser {
    id: string;
    displayName: string;
    uniqueName: string;
}

export interface ISprintCapacityOptions {
    /** Account current date */
    accountCurrentDate: string;

    /** Allowed activities such as 'development', 'testing', etc.. */
    allowedActivities: string[];

    /** Configured weekend days array */
    weekends: number[];

    /** Is capacity options editable */
    isEditable: boolean;

    /** Acitivty field reference name */
    activityFieldReferenceName: string;

    /** Activity field display name */
    activityFieldDisplayName: string;

    /** Assigned-to field display name */
    assignedToFieldDisplayName: string;

    /** RemainingWork suffix format (similar to '{0}h') */
    remainingWorkSuffixFormat: string;
}

/** Taskboard data from the data provider */
export interface ISprintTaskboardData {
    /** Model used to render the taskboard view */
    taskboardModel: ITaskboardModelOptions;

    /** Board card settings for this taskboard */
    boardCardSettings: IBoardCardSettings;

    /** Iteration for this taskboard */
    iterationId: string;

    /** Iteration path for this taskboard */
    iterationPath: string;

    /** Work item types available to add for this taskboard.  Used in the Hub Header add new item command. */
    newWorkItemTypes: string[];

    /** Initial settings to use for the taskboard filter */
    initialFilterState: string;

    /** Initial settings to use for the People Switcher */
    initialPeopleSwitcherState: string;

    /** Url for connecting to SignalR hubs. */
    signalrHubUrl: string;
}

export interface ISprintTaskboardErrorData {
    /** Exception info */
    exceptionInfo: ExceptionInfo;
}

export type ColumnToStateMap = { [columnId: string]: { [workItemTypeName: string]: string /** state */ } };
export type TaskWorkItemTypesToStateMap = { [workItemTypeName: string]: { [stateName: string]: WorkItemStateCategory } };
export type StateTransitions = { [key: string]: { [key: string]: string[] } };

/** Taskboard model options interface */
export interface ITaskboardModelOptions {
    /** Id of the team the taskboard is shown for */
    team: ITeam;

    /** Child workItemTypeNames */
    childWorkItemTypes: string[];

    /** List of fieldDefinitions */
    fieldDefinitions: ICardFieldDefinition[];

    /** Initial filters */
    filters: ITaskboardFilter[];

    /**
     * Flag indicating whether there are any nested tasks on current board.
     * Reordering is disabled when there are nested tasks.
     */
    hasNestedTasks: boolean;

    /** Order by field (such as stack rank) */
    orderByField: string;

    /** Parent ids */
    parentIds: number[];

    /** Parent workItem plural name */
    parentNamePlural: string;

    /** Taskboard content payload */
    payload: ITaskboardPayload;

    /** Ids of work items causing re-ordering to be disabled due to same type hierarchy. These items are visible */
    reorderBlockingWorkItemIds?: number[];

    /** Ids of work items missing from the taskboard due to same type hierarchy*/
    missingWorkItemIds?: number[];

    /** Remaining work suffix format (such as '{0} h') */
    remainingWorkFormat: string;

    /** List of states */
    states: string[];

    /** Default team field value */
    teamFieldDefaultValue: string;

    /** Team fieldReference name */
    teamFieldRefName: string;

    /** State transitions dictionary */
    transitions: StateTransitions;

    /** Rollup field reference name */
    workRollupField: string;

    /** Optional tfsContext */
    tfsContext?: any;

    areColumnsCustomized?: boolean;

    /** List of columns */
    columns: IColumn[];

    /** column to state map */
    columnIdToWorkItemTypeStateMap: ColumnToStateMap;

    /** Available states and state category for task work item types */
    taskWorkItemTypesToStateMap: TaskWorkItemTypesToStateMap;

    /** Column info by work item id */
    workItemColumnIdMap?: { [workItemId: number]: string /** columnId */ };

    /** false when the column mapping is not valid */
    isColumnMappingValid?: boolean;

    /** Specifies the error when isColumnMappingValid is false */
    columnMappingsValidationMessage?: string;

    /** describes if the taskboard is empty */
    isEmpty?: boolean;

    /** list of fields that can be filtered on */
    filterableFieldNames: string[];

    /** mapping of parents to children */
    parentMappings?: ParentChildWIMap[];

    enabledWorkItemTypes: string[];
}

export interface IBoardCardSettings {
    scope: string;
    scopeId: string;
    cards: { [key: string]: { [key: string]: string }[] };
    styles: IStyleRule[];
}

/**
 * Exception info
 */
export interface ExceptionInfo {
    exceptionMessage: string;
    exceptionMessageCustomRender?: (message: string) => JSX.Element;
    additionalMessages?: string[];
    additionalMessagesCustomRender?: (message: string) => JSX.Element;
    primaryLinkText?: string;
    primaryLinkHref?: string;
    secondaryLinkText?: string;
    secondaryLinkHref?: string;
    isSettingsException?: boolean;
    stackTrace?: string;
}

export interface ICardFieldDefinition {
    Id: number;
    ReferenceName: string;
    Name: string;
    Type: CardFieldType;
    IsEditable: boolean;
    IsIdentity: boolean;
}

export interface ITaskboardFilter {
    fieldName: string;
    values: string[];
    selectedValue: string;
}

/** TaskBoard Payload */
export interface ITaskboardPayload {
    data: { [key: number]: (string | number)[] };
    fieldRefNames: string[];
}

export interface IColumn {
    id: string;
    name: string;
    order: number;
}

export interface IStyleRule {
    name: string;
    type: string;
    isEnabled: boolean;
    styles?: { [key: string]: string };
    criteria?: IItemQuery;
}

export interface IItemQuery {
    clauses: FilterClause[];
    groups: IQueryGroup[];
    maxGroupLevel: number;
}

export interface IQueryClause {
    fieldName: string;
    index: number;
    logicalOperator: string;
    operator: string;
    value: any;
}

export interface IQueryGroup {
    end: number;
    start: number;
    level: number;
}

export interface ITaskboardWorkItemChangedPayload {
    id: number;
    changeType: WorkItemSignalREventType;
    revision: number;
    stackRank: string;
    iterationPath: string;
    parentId: number;
    parentChangeType: string;
}

export interface ITaskboardWorkItemColumnChangedPayload {
    workItemId: number;
    columnId: string;
    projectId: number;
    teamId: string;
}

export type TaskboardWorkItemChangedCallback = (payloads: ITaskboardWorkItemChangedPayload[]) => void;
export type TaskboardWorkItemColumnChangedCallback = (payloads: ITaskboardWorkItemColumnChangedPayload[]) => void;
export type TaskboardChangedDefaultCallback = () => void;
